import { AlertTriangle, Lock } from 'lucide-react';
import type { DelinquencyStatus } from '../../../domain/enums/SystemEnums';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { daysUntil } from '../../../domain/formatters/date';
import { toBengaliNumerals } from '../../../domain/formatters/currency';
import './DelinquencyBanner.css';

export interface DelinquencyBannerProps {
  status: DelinquencyStatus;
  nextDueDate: string | null;
}

/**
 * W02 + W14 — persistent delinquency banner docked under the top bar.
 * IN_NOTICE_PERIOD: amber with grace countdown; DISABLED_DEFAULT: crimson + restriction copy.
 */
export function DelinquencyBanner({ status, nextDueDate }: DelinquencyBannerProps) {
  const { t, locale } = useTranslation();
  if (status !== 'IN_NOTICE_PERIOD' && status !== 'DISABLED_DEFAULT' && status !== 'INACTIVE') return null;

  const days = daysUntil(nextDueDate);
  const countdown =
    days !== null && status === 'IN_NOTICE_PERIOD'
      ? ` — ${t('delinquency.grace')}: ${locale === 'bn' ? toBengaliNumerals(String(days)) : days}d`
      : '';

  const bannerText =
    status === 'INACTIVE'
      ? 'Account Inactive (Overdue >90 Days) — Apartment investment features locked. Clear arrears to reactivate.'
      : status === 'DISABLED_DEFAULT'
        ? t('delinquency.disabled')
        : t('delinquency.notice') + countdown;

  return (
    <div
      className={`delinquency-banner delinquency-${status.toLowerCase()}`}
      role="alert"
      data-testid="delinquency-banner"
    >
      <AlertTriangle size={16} aria-hidden />
      <span>{bannerText}</span>
      {(status === 'DISABLED_DEFAULT' || status === 'INACTIVE') && <Lock size={14} aria-hidden />}
    </div>
  );
}
